import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  DatePicker,
  HorizontalGrid,
  HorizontalStack,
  LegacyCard,
  OptionList,
  Popover,
  Scrollable,
  Select,
  useBreakpoints,
  VerticalStack,
  Icon,
} from "@shopify/polaris";
import { CalendarMinor } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";

const formatDate = (date) => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return [year, month, day].join("-");
};

const RangePicker = (props) => {
  const { t } = useTranslation();
  const { mdDown, lgUp } = useBreakpoints();
  const [customDate, setCustomDate] = useState(true);
  const shouldShowMultiMonth = lgUp;
  const ranges = useMemo(() => {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const yesterday = new Date(
      new Date(new Date().setDate(today.getDate() - 1)).setHours(0, 0, 0, 0)
    );
    return [
      {
        title: t("Today"),
        alias: "today",
        period: {
          since: today,
          until: today,
        },
      },
      {
        title: t("Yesterday"),
        alias: "yesterday",
        period: {
          since: yesterday,
          until: yesterday,
        },
      },
      {
        title: t("Last_7_days"),
        alias: "last7days",
        period: {
          since: new Date(
            new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)
          ),
          until: yesterday,
        },
      },
      {
        title: t("Last_30_days"),
        alias: "last30days",
        period: {
          since: new Date(
            new Date(new Date().setDate(today.getDate() - 30)).setHours(
              0,
              0,
              0,
              0
            )
          ),
          until: yesterday,
        },
      },
    ];
  }, [t]);
  const [popoverActive, setPopoverActive] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(ranges[0]);
  const [inputValues, setInputValues] = useState({});
  const [{ month, year }, setDate] = useState({
    month: activeDateRange.period.since.getMonth(),
    year: activeDateRange.period.since.getFullYear(),
  });
  const datePickerRef = useRef(null);
  function handleMonthChange(month, year) {
    setDate({ month, year });
  }
  function handleCalendarChange({ start, end }) {
    const newDateRange = ranges.find((range) => {
      return (
        range.period.since.valueOf() === start.valueOf() &&
        range.period.until.valueOf() === end.valueOf()
      );
    }) || {
      alias: "custom",
      title: "Custom",
      period: {
        since: start,
        until: end,
      },
    };
    setActiveDateRange(newDateRange);
  }
  function apply() {
    sessionStorage.setItem("startDate", inputValues.since);
    sessionStorage.setItem("endDate", inputValues.until);
    props?.handleOrdersFilter(
      sessionStorage.getItem("orderStatus"),
      null,
      inputValues.since,
      inputValues.until
    );
    setPopoverActive(false);
    setCustomDate(true);
  }
  function cancel() {
    setPopoverActive(false);
  }
  useEffect(() => {
    if (activeDateRange) {
      setInputValues({
        since: formatDate(activeDateRange.period.since),
        until: formatDate(activeDateRange.period.until),
      });
      function monthDiff(referenceDate, newDate) {
        return (
          newDate.month -
          referenceDate.month +
          12 * (referenceDate.year - newDate.year)
        );
      }
      const monthDifference = monthDiff(
        { year, month },
        {
          year: activeDateRange.period.until.getFullYear(),
          month: activeDateRange.period.until.getMonth(),
        }
      );
      if (monthDifference > 1 || monthDifference < 0) {
        setDate({
          month: activeDateRange.period.until.getMonth(),
          year: activeDateRange.period.until.getFullYear(),
        });
      }
    }
  }, [activeDateRange, month, year]);
  const buttonValue =
    activeDateRange.title === "Custom"
      ? activeDateRange.period.since.toDateString() +
        " - " +
        activeDateRange.period.until.toDateString()
      : activeDateRange.title;

  useEffect(() => {
    setActiveDateRange(ranges[0]);
    sessionStorage.removeItem("endDate");
    sessionStorage.removeItem("startDate");
    // A reset intentionally returns to the range translated for this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.resetDate]);
  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <button
          type="button"
          className="dateContainerField"
          onClick={() => setPopoverActive(!popoverActive)}
        >
          <div className="d-flex justify-content-between ">
            <p>{buttonValue}</p>
            <p>
              <Icon source={CalendarMinor} color="base" />
            </p>
          </div>
        </button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <HorizontalGrid
          columns={{
            xs: "1fr",
            mdDown: "1fr",
            md: "max-content max-content",
          }}
          gap={0}
          ref={datePickerRef}
        >
          {customDate ? (
            <Box
              maxWidth={mdDown ? "516px" : "212px"}
              width={mdDown ? "100%" : "212px"}
              padding={{ xs: 5, md: 0 }}
              paddingBlockEnd={{ xs: 1, md: 0 }}
            >
              {mdDown ? (
                <div className="d-flex gap-10">
                  <Select
                    label="dateRangeLabel"
                    labelHidden
                    onChange={(value) => {
                      const result = ranges.find(
                        ({ title, alias }) => title === value || alias === value
                      );
                      setActiveDateRange(result);
                    }}
                    value={
                      activeDateRange?.title || activeDateRange?.alias || ""
                    }
                    options={ranges.map(({ alias, title }) => title || alias)}
                  />
                  <Button
                    size="slim"
                    onClick={() => {
                      setCustomDate(false);
                    }}
                  >
                    {t("Custom")}
                  </Button>
                </div>
              ) : (
                <Scrollable style={{ height: "" }}>
                  <OptionList
                    options={ranges.map((range) => ({
                      value: range.alias,
                      label: range.title,
                    }))}
                    selected={activeDateRange.alias}
                    onChange={(value) => {
                      setActiveDateRange(
                        ranges.find((range) => range.alias === value[0])
                      );
                    }}
                  />
                  <LegacyCard sectioned>
                    <Button
                      size="slim"
                      onClick={() => {
                        setCustomDate(false);
                      }}
                    >
                      {t("Custom")}
                    </Button>
                  </LegacyCard>
                </Scrollable>
              )}
            </Box>
          ) : (
            <Box padding={{ xs: 5 }} maxWidth={mdDown ? "320px" : "516px"}>
              <VerticalStack gap="4">
                <div>
                  <DatePicker
                    month={month}
                    year={year}
                    selected={{
                      start: activeDateRange.period.since,
                      end: activeDateRange.period.until,
                    }}
                    onMonthChange={handleMonthChange}
                    onChange={handleCalendarChange}
                    multiMonth={shouldShowMultiMonth}
                    allowRange
                  />
                </div>
              </VerticalStack>
            </Box>
          )}
        </HorizontalGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <HorizontalStack align="end">
            <Button onClick={cancel}>{t("Cancel")}</Button>
            <Button primary onClick={apply}>
              {t("Apply")}
            </Button>
          </HorizontalStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
};

export default RangePicker;
